import { Module } from 'vuex'
import GoogleTagManagerState from '../types/GoogleTagManagerState'
import { CheckoutCart, CheckoutStepPayload } from '../types/CheckoutEcommerce'
import { isEnabled, getProduct, getCheckoutEcommerce, claimOncePerCheckout } from '../helpers'
import config from 'config'
import Vue from 'vue'
import VueGtm from 'vue-gtm'

const CHECKOUT_EVENT = 'checkout'
const ADD_SHIPPING_INFO_EVENT = 'add_shipping_info'
const ADD_PAYMENT_INFO_EVENT = 'add_payment_info'

/**
 * @return {VueGtm|null}
 */
const resolveGtm = (): VueGtm | null => {
  if (!isEnabled(config.googleTagManager.id)) {
    return null
  }

  return (Vue as any).gtm || null
}

/**
 * @param {VueGtm} gtm
 * @return {void}
 */
const resetEcommerce = (gtm: VueGtm): void => {
  const dataLayer = gtm.dataLayer()

  if (dataLayer) {
    dataLayer.push({ ecommerce: null })
  }
}

/**
 * @param {any} rootGetters
 * @return {CheckoutCart}
 */
const resolveCart = (rootGetters: any): CheckoutCart => {
  return {
    items: rootGetters['cart/getCartItems'],
    token: rootGetters['cart/getCartToken'],
    get totals () {
      return rootGetters['cart/getTotals']
    }
  }
}

/**
 * @param {CheckoutCart} cart
 * @param {string} eventName
 * @param {string} variant
 * @param {Record<string, any>} params
 * @return {void}
 */
const pushCheckoutStepEvent = (
  cart: CheckoutCart,
  eventName: string,
  variant: string,
  params: Record<string, any>
): void => {
  if (!cart.items || !cart.items.length) {
    return
  }

  const gtm = resolveGtm()
  if (!gtm) {
    return
  }

  if (!claimOncePerCheckout(eventName, cart.token)) {
    return
  }

  resetEcommerce(gtm)

  gtm.trackEvent({
    event: eventName,
    checkout_variant: variant,
    ecommerce: {
      ...getCheckoutEcommerce(cart.items, cart.totals),
      ...params
    }
  })
}

export const googleTagManagerModule: Module<GoogleTagManagerState, any> = {
  namespaced: true,
  state: {
    key: null
  },
  actions: {
    async checkoutEvent (context, { products, variant }: { products: Record<string, any>[], variant: string }) {
      const gtm = resolveGtm()
      if (!gtm) {
        return
      }

      resetEcommerce(gtm)

      gtm.trackEvent({
        event: CHECKOUT_EVENT,
        checkout_variant: variant,
        ecommerce: {
          items: (products || []).map(product => getProduct(product))
        }
      })
    },
    async addShippingInfoEvent ({ rootGetters }, { variant, shippingTier }: CheckoutStepPayload) {
      pushCheckoutStepEvent(
        resolveCart(rootGetters),
        ADD_SHIPPING_INFO_EVENT,
        variant,
        shippingTier ? { shipping_tier: shippingTier } : {}
      )
    },
    async addPaymentInfoEvent ({ rootGetters }, { variant, paymentType }: CheckoutStepPayload) {
      pushCheckoutStepEvent(
        resolveCart(rootGetters),
        ADD_PAYMENT_INFO_EVENT,
        variant,
        paymentType ? { payment_type: paymentType } : {}
      )
    },
    async trackEvent ({ dispatch, commit }, event) {
      if (isEnabled(config.googleTagManager.id)) {
        const GTM: VueGtm = (Vue as any).gtm;
        GTM.trackEvent(event);
      }
    }
  }
}
